import { useCallback, useEffect, useMemo, useState } from 'react';
import { useActiveDocumentInfoContext } from '../../../shared/hooks/ActiveDocumentProvider';
import {
  buildExportRowHighlightStorageScope,
  loadExportRowHighlightMap,
  saveExportRowHighlightMap,
  setExportRowHighlightColor,
  type ExportRowHighlightColor,
  type ExportRowHighlightMap,
} from '../lib/rowHighlights';

export function useExportRowHighlights(): {
  rowHighlightColors: ExportRowHighlightMap;
  handleRowHighlightColorChange: (systemName: string, color: ExportRowHighlightColor | null) => void;
  handleRowHighlightReset: () => void;
} {
  const activeDocumentInfo = useActiveDocumentInfoContext();
  const rowHighlightScope = useMemo(
    () => buildExportRowHighlightStorageScope({
      projectKey: activeDocumentInfo.projectKey,
    }),
    [activeDocumentInfo.projectKey]
  );
  const [rowHighlightColors, setRowHighlightColors] = useState<ExportRowHighlightMap>({});

  useEffect(() => {
    setRowHighlightColors(loadExportRowHighlightMap(rowHighlightScope));
  }, [rowHighlightScope]);

  const handleRowHighlightColorChange = useCallback((systemName: string, color: ExportRowHighlightColor | null) => {
    setRowHighlightColors((previous) => {
      const next = setExportRowHighlightColor(previous, systemName, color);
      saveExportRowHighlightMap(rowHighlightScope, next);
      return next;
    });
  }, [rowHighlightScope]);

  const handleRowHighlightReset = useCallback(() => {
    setRowHighlightColors({});
    saveExportRowHighlightMap(rowHighlightScope, {});
  }, [rowHighlightScope]);

  return { rowHighlightColors, handleRowHighlightColorChange, handleRowHighlightReset };
}
